import React, { useState, useMemo, Suspense } from "react";
import {
  Box,
  Image,
  Flex,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  useDisclosure,
  SlideFade,
  Fade,
  Spinner,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import MenuUnderImage from "./images/Background/MenuUnder.png";
import MenuTop from "./images/Menu/MenuTop.png";
import MenuTrailer from "./images/Menu/MenuTtailer.png";
import MenuAbout from "./images/Menu/MenuAbout.png";
import MenuSchedule from "./images/Menu/MenuSchedule.png";
import MenuTicket from "./images/Menu/MenuTicket.png";
import MenuPerformer from "./images/Menu/MenuPerformer.png";
import Trailer from "./components/Trailer";
import Story from "./components/Story";
import Schedule from "./components/Schedule";
import Cautions from "./components/Cautions";
import Loading from "./components/Loading";

interface MenuItem {
  src: string;
  alt: string;
}

const images = {
  MenuUnderImage,
  MenuTop,
  MenuTrailer,
  MenuAbout,
  MenuSchedule,
  MenuTicket,
  MenuPerformer,
};

const menuItemsData: MenuItem[] = [
  { src: images.MenuTop, alt: "top" },
  { src: images.MenuTrailer, alt: "trailer" },
  { src: images.MenuAbout, alt: "about" },
  { src: images.MenuSchedule, alt: "schedule" },
  { src: images.MenuTicket, alt: "ticket" },
  { src: images.MenuPerformer, alt: "performer" },
];

const preventDefault = (e: MouseEvent): void => {
  e.preventDefault();
};

const colors = {
  brown: "rgb(60, 17, 0)",
};

const App = () => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const menuItems = useMemo(() => menuItemsData, []);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const scrollToTrailer = (): void => {
    // 一時的に、trailer-title要素にスクロールする
    // HLS動画が再度有効になった場合は、trailer-video要素に戻す
    const element = document.getElementById("trailer-title");
    const offset = 30;
    const elementPosition = element?.getBoundingClientRect().top ?? 0;
    const offsetPosition = elementPosition + window.scrollY - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const scrollToStory = (): void => {
    const element = document.getElementById("story-section");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToSchedule = (): void => {
    const element = document.getElementById("schedule-section");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTicket = (): void => {
    const element = document.getElementById("ticket-section");
    const offset = 100;
    const elementPosition = element?.getBoundingClientRect().top ?? 0;
    const offsetPosition = elementPosition + window.scrollY - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const scrollToPerformer = (): void => {
    const element = document.getElementById("performer-section");
    const offset = 100;
    const elementPosition = element?.getBoundingClientRect().top ?? 0;
    const offsetPosition = elementPosition + window.scrollY - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const scrollToTop = (): void => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  React.useEffect(() => {
    document.addEventListener("contextmenu", preventDefault);
    return () => {
      document.removeEventListener("contextmenu", preventDefault);
    };
  }, []);

  React.useEffect(() => {
    // ページの読み込みが完了したらローディングを非表示
    window.addEventListener("load", () => {
      setIsLoading(false);
    });

    // バックアップとして、一定時間後にもローディングを非表示
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    // メニューを遅延表示
    const menuTimer = setTimeout(() => {
      setIsMenuVisible(true);
    }, 1000); // 秒後にメニューを表示

    return () => {
      clearTimeout(loadingTimer);
      clearTimeout(menuTimer);
    };
  }, []);

  return (
    <>
      <Suspense fallback={<Spinner />}>
        {isLoading && <Loading isPageLoaded={!isLoading} />}
      </Suspense>
      <Box
        bg={{
          base: "transparent",
          md: colors.brown,
        }}
        w="100%"
        pos="relative"
        h={{
          base: "120px",
          sm: "120px",
          md: "60px",
          lg: "70px",
          xl: "70px",
          "2xl": "80px",
        }}
      >
        <Fade in={isMenuVisible} transition={{ enter: { duration: 0.5 } }}>
          <Box
            pos="fixed"
            top={{
              base: "20px",
              sm: "20px",
              md: "10px",
              lg: "20px",
            }}
            left={0}
            right={0}
            zIndex={1000}
          >
            {/* モバイル用ハンバーガーメニュー */}
            {!isOpen && (
              <Box
                position="fixed"
                top={0}
                right={0}
                p={4}
                zIndex={20}
                display={{
                  base: "block",
                  sm: "block",
                  md: "none",
                }}
              >
                <IconButton
                  aria-label="Menu"
                  icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
                  onClick={onOpen}
                  bg="black"
                  color="white"
                  _hover={{ bg: "black" }}
                  w="30px"
                  h="30px"
                  fontSize="20px"
                />
              </Box>
            )}

            {/* モバイル用ドロワーメニュー */}
            <Drawer
              isOpen={isOpen}
              placement="right"
              onClose={onClose}
              size="full"
              blockScrollOnMount={false}
              children={
                <>
                  <DrawerOverlay
                    zIndex={1000}
                    display={{ base: "block", sm: "block", md: "none" }}
                  />
                  <DrawerContent
                    zIndex={1001}
                    bg={{ base: "transparent", md: colors.brown }}
                    display={{ base: "block", sm: "block", md: "none" }}
                  >
                    {/* ドロワーの中に閉じるボタンを配置 */}
                    <Box
                      position="absolute"
                      top={{
                        base: "20px",
                        sm: "20px",
                        md: "10px",
                        lg: "20px",
                      }}
                      right={{
                        base: "20px",
                        sm: "20px",
                        md: "20px",
                        lg: "20px",
                      }}
                      zIndex={1100}
                    >
                      <IconButton
                        icon={<CloseIcon />}
                        onClick={onClose}
                        aria-label="Close menu"
                        bg="black"
                        color="white"
                        _hover={{ bg: "gray.800" }}
                        w={{
                          base: "30px",
                          sm: "30px",
                          md: "45px",
                          lg: "auto",
                        }}
                        h={{
                          base: "30px",
                          sm: "30px",
                          md: "45px",
                          lg: "auto",
                        }}
                        fontSize={{
                          base: "16px",
                          sm: "16px",
                          md: "20px",
                          lg: "24px",
                        }}
                      />
                    </Box>

                    <DrawerBody pt={10}>
                      <Flex direction="column" align="center" gap={8}>
                        {menuItems.map((item, index) => (
                          <SlideFade
                            key={index}
                            in={isOpen}
                            transition={{
                              enter: {
                                delay: 0.3 + index * 0.1,
                                duration: 0.4,
                              },
                            }}
                            offsetY="-20px"
                          >
                            <Image
                              src={item.src}
                              alt={item.alt}
                              h="auto"
                              maxH="40px"
                              cursor="pointer"
                              onClick={() => {
                                if (item.alt === "top") scrollToTop();
                                if (item.alt === "trailer") scrollToTrailer();
                                if (item.alt === "about") scrollToStory();
                                if (item.alt === "schedule") scrollToSchedule();
                                if (item.alt === "ticket") scrollToTicket();
                                if (item.alt === "performer")
                                  scrollToPerformer();
                                onClose();
                              }}
                            />
                          </SlideFade>
                        ))}
                      </Flex>
                    </DrawerBody>
                  </DrawerContent>
                </>
              }
            />

            {/* デスクトップ用メニュー用の親Box */}
            <Box
              display={{ base: "none", md: "block" }}
              position="relative"
              h={{
                md: "45px",
                lg: "50px",
                xl: "55px",
                "2xl": "60px",
              }}
              mt={{
                md: "-3px",
                lg: "-10px",
                xl: "-13px",
                "2xl": "-10px",
              }}
            >
              {/* MenuUnderImage */}
              <Box
                w="100%"
                h="100%"
                display={{ base: "none", md: "flex" }}
                justifyContent="center"
                position="relative"
                zIndex={5}
              >
                <Image
                  src={images.MenuUnderImage}
                  alt="menu"
                  display="block"
                  opacity={0.8}
                  w={{
                    md: "65%",
                    lg: "65%",
                    xl: "65%",
                    "2xl": "65%",
                  }}
                  h="100%"
                  maxW={{
                    md: "650px",
                    lg: "800px",
                    xl: "1000px",
                    "2xl": "1200px",
                  }}
                />
              </Box>

              {/* デスクトップ用メニュー */}
              <Flex
                justify="center"
                pos="absolute"
                top="50%"
                left={0}
                right={0}
                transform="translateY(-50%)"
                gap={{
                  base: 14,
                  sm: 14,
                  md: 4,
                  lg: 6,
                  xl: 8,
                  "2xl": 14,
                }}
                zIndex={10}
                display={{ base: "none", md: "flex" }}
              >
                {menuItems.map((item, index) => (
                  <Image
                    key={index}
                    src={item.src}
                    alt={item.alt}
                    h="auto"
                    maxH={{
                      base: "50px",
                      sm: "50px",
                      md: "20px",
                      lg: "25px",
                      xl: "30px",
                      "2xl": "50px",
                    }}
                    cursor="pointer"
                    onClick={() => {
                      if (item.alt === "top") scrollToTop();
                      if (item.alt === "trailer") scrollToTrailer();
                      if (item.alt === "about") scrollToStory();
                      if (item.alt === "schedule") scrollToSchedule();
                      if (item.alt === "ticket") scrollToTicket();
                      if (item.alt === "performer") scrollToPerformer();
                    }}
                  />
                ))}
              </Flex>
            </Box>
          </Box>
        </Fade>
        <Box
          pt={{
            base: "0px",
            sm: "0px",
            md: "60px",
            lg: "70px",
            xl: "70px",
            "2xl": "80px",
          }}
          maxW="100vw"
          overflowX="hidden"
          overflowY="hidden"
        >
          <Box id="trailer-section">
            <Trailer />
          </Box>
          <Box id="story-section">
            <Story />
          </Box>
          <Box id="schedule-section">
            <Schedule />
          </Box>
          <Cautions />
        </Box>
      </Box>
    </>
  );
};

export default App;
